"use client"

import {
  getCurrentTrackingSearch,
  getHashAnchor,
  mergeTrackingParams,
  persistUtmParams,
} from "common/bit/utils/utmParams"
import { initAuth } from "components/shopFeatures/auth/authSlice"
import { ContextProviders } from "contexts/ContextProviders"
import { usePathname, useSearchParams } from "next/navigation"
import { Suspense, useEffect, useRef } from "react"
import { Provider } from "react-redux"
import { makeStore, useAppDispatch } from "store"
import styled from "styled-components"
import colors from "theme/colors"
import ThemeProviders from "theme/ThemeProviders"
import AnalyticsWrapper from "./analytics-wrapper"

const ENV = process.env.ENV

const StyledEnvironmentName = styled.pre`
  background-color: ${colors.mazdaRed};
  color: ${colors.white};
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999999999999;
  margin: 0;
  opacity: 0.8;
  pointer-events: none;
`

const COMMBOX_ID = process.env.COMMBOX_ID
if (!COMMBOX_ID) {
  throw new Error("Please provide the COMMBOX_ID environment variable.")
}

// Create a singleton store for the App Router (no getInitialProps hydration required)

// Initialize auth state on app load
function AuthInitializer() {
  const dispatch = useAppDispatch()

  useEffect(() => {
    // Only run on client side
    if (typeof window !== "undefined") {
      const token = localStorage.getItem("token")
      dispatch(initAuth(token))
      persistUtmParams()
    }
  }, [dispatch])

  return null
}

function TrackingParamsInUrlSync() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (typeof window === "undefined") return

    const trackingSearch = getCurrentTrackingSearch()
    if (!trackingSearch) return

    const currentSearch = searchParams?.toString() || ""
    const mergedSearch = mergeTrackingParams(currentSearch, trackingSearch)
    if (mergedSearch === currentSearch) return

    const hash = getHashAnchor()
    const nextUrl = mergedSearch
      ? `${pathname}?${mergedSearch}${hash}`
      : `${pathname}${hash}`

    window.history.replaceState(window.history.state, "", nextUrl)
  }, [pathname, searchParams])

  return null
}

export default function ClientProviders({
  children,
  preloadedState,
}: {
  children: React.ReactNode
  preloadedState?: any
}) {
  const storeRef = useRef(makeStore(preloadedState))

  return (
    <Provider store={storeRef.current}>
      <AuthInitializer />
      <Suspense fallback={null}>
        <TrackingParamsInUrlSync />
      </Suspense>
      <ThemeProviders>
        <ContextProviders>{children}</ContextProviders>
      </ThemeProviders>

      {ENV && ENV !== "production" && (
        <StyledEnvironmentName>{ENV}</StyledEnvironmentName>
      )}

      {/* {false && ENV && ENV === "development" && (
        <NextLink href="/ds">
          <StyledDSButton>
            <span>DS and Reusable</span>
          </StyledDSButton>
        </NextLink>
      )} */}

      <Suspense fallback={null}>
        <AnalyticsWrapper />
      </Suspense>
    </Provider>
  )
}
